import { ListPlus, ScanSearch, Bookmark, Undo2 } from 'lucide-react';
import ItemImage from './ItemImage';
import { LaneChip, DiagnosisChip, PriorityBadge, SimBadge, ReasonChips } from './Tags';
import ShortlistPicker from '../shortlists/ShortlistPicker';
import { Hint } from '../ui/tooltip';
import { fmtEur, fmtInt, fmtDeltaPct, fmtShopPrice, discountPct, variantLabel } from '../../lib/format';
import { cn } from '../../lib/utils';

function Stat({ label, value, hint, muted }) {
  const body = (
    <div className="min-w-0 px-1 py-1.5">
      <dt className="truncate text-[10px] text-muted-foreground">{label}</dt>
      <dd className={cn('truncate text-[12px] font-medium tabular-nums', muted && 'text-muted-foreground')}>{value}</dd>
    </div>
  );
  return hint ? <Hint text={hint}>{body}</Hint> : body;
}

/** Months of stock at the 6-month sales speed, in words. */
function stockLasts(months, sold6m) {
  if (!sold6m || months == null) return 'No sales';
  if (months >= 24) return `${(months / 12).toFixed(1)} yrs`;
  return `${Math.max(1, Math.round(months))} mo`;
}

export default function ItemCard({ item, onOpen, onFindSimilar, selectable, selected, onToggleSelect, footer }) {
  const open = () => onOpen?.(item);
  const discount = discountPct(item.price, item.rrp);
  return (
    <article
      className={cn(
        'group relative flex flex-col overflow-hidden rounded-lg border bg-card transition hover:border-foreground/25',
        selected && 'border-primary ring-1 ring-primary'
      )}
    >
      <button
        type="button"
        onClick={open}
        className="relative block aspect-[4/3] w-full overflow-hidden border-b bg-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-ring"
        aria-label={`Open ${item.title}`}
      >
        <ItemImage src={item.image_url} alt={item.title} className="absolute inset-0 h-full w-full p-3" rounded="rounded-none" iconClassName="h-10 w-10" imgClassName="transition-transform duration-300 group-hover:scale-[1.03]" />
        <div className="absolute left-2 top-2 flex flex-wrap gap-1">
          {item.priority && (
            <PriorityBadge
              priority={item.priority}
              reason={item.priority_reason}
              short
              className={cn('dark:bg-white dark:ring-slate-200', item.priority === 'Act now' ? 'dark:bg-red-600 dark:text-white dark:ring-red-600' : 'dark:text-slate-800')}
            />
          )}
          {item.shortlist_count > 0 && (
            <Hint text={`On ${item.shortlist_count} shortlist${item.shortlist_count > 1 ? 's' : ''}`}>
              <span className="inline-flex items-center gap-0.5 rounded-md bg-white/90 px-1.5 py-0.5 text-[11px] font-medium text-slate-700 ring-1 ring-inset ring-slate-200">
                <Bookmark className="h-3 w-3 fill-current" />
                {item.shortlist_count}
              </span>
            </Hint>
          )}
        </div>
        <div className="absolute bottom-2 left-2 flex gap-1">
          {item.visual_sim != null && <SimBadge value={item.visual_sim} />}
          {item.match_score != null && (
            <span className="rounded-md bg-white/90 px-1.5 py-0.5 text-[11px] font-medium tabular-nums text-slate-700 ring-1 ring-inset ring-slate-200">
              match {Math.round(Number(item.match_score) * (item.match_score <= 1 ? 100 : 1))}
            </span>
          )}
        </div>
      </button>

      {/* floating actions */}
      <div className="absolute right-2 top-2 flex flex-col gap-1 opacity-100 transition sm:opacity-0 sm:group-hover:opacity-100 sm:group-focus-within:opacity-100">
        <ShortlistPicker ids={[item.odoo_product_id]}>
          <button
            type="button"
            className="flex h-8 w-8 items-center justify-center rounded-full border bg-white/95 text-slate-700 shadow-sm hover:bg-white hover:text-slate-950"
            aria-label="Add to shortlist"
            title="Add to shortlist"
          >
            <ListPlus className="h-4 w-4" />
          </button>
        </ShortlistPicker>
        {onFindSimilar && (
          <Hint text="Find similar look" side="left">
            <button
              type="button"
              onClick={() => onFindSimilar(item)}
              className="flex h-8 w-8 items-center justify-center rounded-full border bg-white/95 text-slate-700 shadow-sm hover:bg-white hover:text-slate-950"
              aria-label="Find similar look"
            >
              <ScanSearch className="h-4 w-4" />
            </button>
          </Hint>
        )}
      </div>

      <div className="flex flex-1 flex-col gap-2 p-3">
        <button type="button" onClick={open} className="min-w-0 text-left focus-visible:outline-none">
          <div className="truncate text-[11px] text-muted-foreground">
            <span className="font-medium uppercase tracking-wide">{item.brand || '—'}</span>
            {variantLabel(item.variant_title) && <> · {variantLabel(item.variant_title)}</>}
          </div>
          <h3 className="line-clamp-2 text-[13.5px] font-medium leading-snug hover:underline" title={item.title}>{item.title}</h3>
        </button>

        <Hint text={discount != null ? `Shop price now, ${discount}% below the recommended retail price (RRP ${fmtShopPrice(item.rrp)}). Prices incl. VAT.` : 'Shop price now, incl. VAT. No discount against the RRP.'}>
          <div className="flex items-baseline gap-1.5 tabular-nums">
            <span className={cn('text-[15px] font-semibold leading-none', discount != null && 'text-red-600 dark:text-red-400')}>{fmtShopPrice(item.price)}</span>
            {discount != null && (
              <>
                <span className="text-xs text-muted-foreground line-through">{fmtShopPrice(item.rrp)}</span>
                <span className="text-[11px] font-medium text-red-600 dark:text-red-400">−{discount}%</span>
              </>
            )}
          </div>
        </Hint>

        <div className="flex min-w-0 items-center gap-1">
          <LaneChip lane={item.exit_lane} reason={item.exit_reason} className="shrink-0" />
          <DiagnosisChip diagnosis={item.diagnosis} reason={item.diagnosis_reason} className="min-w-0 truncate" />
          {item.supplier_return_candidate && (
            <Hint text="Ask supplier: bought in the last 12 months from a supplier we still order from, worth asking about a return or exchange (not confirmed).">
              <span className="ml-auto flex h-5 w-5 shrink-0 items-center justify-center rounded-full border text-muted-foreground">
                <Undo2 className="h-3 w-3" />
              </span>
            </Hint>
          )}
        </div>

        {/* stock, rotation and money in one row */}
        <dl className="grid grid-cols-4 divide-x rounded-md border text-center">
          <Stat label="Stock" value={fmtInt(item.free_qty)} hint="Pieces in stock right now" />
          <Stat
            label="Sold 6/12M"
            value={`${fmtInt(item.sold_6m)} / ${fmtInt(item.sold_12m)}`}
            muted={!item.sold_12m}
            hint={`Pieces sold in the last 6 and 12 months, all channels. Stock lasts: ${stockLasts(item.months_cover, item.sold_6m)}.`}
          />
          <Stat label="Cost" value={fmtEur(item.unit_cost)} hint="Purchase price per piece, ex VAT" />
          <Stat label="Min." value={fmtEur(item.breakeven_price)} hint="Lowest shop price (incl. VAT) at which we do not lose money: purchase cost + €10 handling, + VAT" />
        </dl>

        <Hint text={`Stock value at purchase cost · days in the warehouse · product page ${item.completeness}% complete${item.missing_fields?.length ? ` (missing: ${item.missing_fields.map((m) => m.replace(/_/g, ' ')).join(', ')})` : ''}`}>
          <div className="flex items-center gap-1.5 truncate text-[11px] tabular-nums text-muted-foreground">
            <span className="font-medium text-foreground">{fmtEur(item.free_value)}</span>
            <span>·</span>
            {item.age_days != null && <><span>{fmtInt(item.age_days)} days</span><span>·</span></>}
            <span className={cn('h-1.5 w-1.5 shrink-0 rounded-full', item.completeness >= 80 ? 'bg-emerald-500' : item.completeness >= 50 ? 'bg-amber-500' : 'bg-red-500')} />
            <span>info {item.completeness}%</span>
          </div>
        </Hint>
        {item.price_delta_pct != null && (
          <div className={cn('text-[11px] tabular-nums', item.price_delta_pct > 0 ? 'text-amber-700 dark:text-amber-400' : 'text-emerald-700 dark:text-emerald-400')}>
            {fmtDeltaPct(item.price_delta_pct)} vs requested product
          </div>
        )}
        {item.match_reasons?.length > 0 && <ReasonChips reasons={item.match_reasons} max={3} />}

        {(selectable || footer) && (
          <div className="mt-auto flex items-center justify-between gap-2 border-t pt-2">
            {selectable && (
              <label className="flex cursor-pointer items-center gap-2 text-xs">
                <input
                  type="checkbox"
                  className="h-4 w-4 rounded border-input accent-[hsl(var(--primary))]"
                  checked={!!selected}
                  onChange={() => onToggleSelect?.(item)}
                />
                Candidate
              </label>
            )}
            {footer}
          </div>
        )}
      </div>
    </article>
  );
}

export function ItemCardSkeleton() {
  return (
    <div className="overflow-hidden rounded-lg border bg-card">
      <div className="aspect-[4/3] animate-pulse bg-muted/60" />
      <div className="space-y-2 p-3">
        <div className="h-3 w-16 animate-pulse rounded bg-muted" />
        <div className="h-4 w-full animate-pulse rounded bg-muted" />
        <div className="h-4 w-2/3 animate-pulse rounded bg-muted" />
        <div className="h-8 animate-pulse rounded bg-muted/70" />
      </div>
    </div>
  );
}
